import posthog from 'posthog-js'

export type PostHogConfig = {
    enabled: boolean
    key: string | null
    host: string
    ui_host?: string | null
}

type AuthUser = {
    id: number
    email: string
    name?: string | null
    lastname?: string | null
    roles?: Array<{ name: string }>
}

type AnalyticsPageProps = {
    auth?: { user?: AuthUser | null }
    tenant?: { id?: string }
    tenant_info?: { tenant_name?: string | null }
    analytics?: { posthog?: PostHogConfig | null }
}

let initialized = false

export function isPostHogEnabled(config?: PostHogConfig | null): config is PostHogConfig {
    return Boolean(config?.enabled && config.key)
}

export function initPostHog(config?: PostHogConfig | null): typeof posthog | null {
    if (!isPostHogEnabled(config) || initialized) {
        return initialized ? posthog : null
    }

    posthog.init(config.key, {
        api_host: config.host,
        ui_host: config.ui_host ?? undefined,
        person_profiles: 'identified_only',
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
        session_recording: {
            maskAllInputs: true,
            maskInputOptions: {
                password: true,
            },
        },
    })

    initialized = true

    return posthog
}

export function syncPostHogUser(props?: AnalyticsPageProps): void {
    if (!initialized) {
        return
    }

    const user = props?.auth?.user

    if (!user) {
        posthog.reset()
        return
    }

    const displayName = [user.name, user.lastname].filter(Boolean).join(' ').trim()
    const tenantId = props?.tenant?.id
    const tenantName = props?.tenant_info?.tenant_name

    posthog.identify(String(user.id), {
        email: user.email,
        name: displayName || user.email,
        roles: user.roles?.map((role) => role.name).join(',') ?? undefined,
        tenant_id: tenantId,
        tenant_name: tenantName ?? undefined,
    })

    if (tenantId) {
        posthog.group('tenant', tenantId, {
            name: tenantName ?? tenantId,
        })
    }
}

export function capturePostHogPageview(url: string): void {
    if (!initialized) {
        return
    }

    posthog.capture('$pageview', {
        $current_url: url,
    })
}

export function getPostHog(): typeof posthog | null {
    return initialized ? posthog : null
}
